import { Button as ButtonPrimitive } from "@base-ui/react/button";
import {
  cva,
  type VariantProps,
} from "class-variance-authority";

import { cn } from "@/infrastructure/lib/utils";

const buttonVariants = cva(
  "focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-none border border-transparent bg-clip-padding text-xs font-medium focus-visible:ring-1 aria-invalid:ring-1 [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground [a]:hover:bg-primary/80",
        outline:
          "border-border bg-background hover:bg-muted hover:text-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 aria-expanded:bg-muted aria-expanded:text-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground dark:hover:bg-muted/50 aria-expanded:bg-muted aria-expanded:text-foreground",
        destructive:
          "bg-destructive/10 hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/20 text-destructive focus-visible:border-destructive/40 dark:hover:bg-destructive/30",
        link: "text-primary underline-offset-4 hover:underline",
        transparent:
          "bg-transparent text-foreground hover:bg-transparent",
        solidDestructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        nav: "transition-colors hover:bg-primary/10 hover:text-primary",
      },
      size: {
        default:
          "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-none px-2 text-xs has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-none px-2.5 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        manage:
          "h-7 gap-2 rounded-none px-2.5 text-sm has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        compact: "h-8 gap-1 px-2.5",
        icon: "size-8",
        "icon-xs":
          "size-6 rounded-none [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-7 rounded-none",
        "icon-lg": "size-9",
      },
      spacing: {
        default: "",
        compact: "gap-1",
        wide: "gap-2",
        padded: "px-3 py-2",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      spacing: "default",
    },
  }
);

// Props for our Button wrapper - includes native button HTML attributes
// and makes nativeButton optional (defaults based on render prop)
type ButtonProps =
  React.ComponentPropsWithoutRef<"button"> &
    VariantProps<typeof buttonVariants> & {
      render?: ButtonPrimitive.Props["render"];
      nativeButton?: boolean;
      focusableWhenDisabled?: boolean;
    };

function Button({
  className,
  variant = "default",
  size = "default",
  spacing = "default",
  render,
  nativeButton,
  ...props
}: ButtonProps) {
  // Determine nativeButton value:
  // - If explicitly provided, use that value
  // - If render is undefined (default button), it's a native button
  // - If render is provided, default to false (can be overridden)
  const isNativeButton =
    nativeButton ?? (render === undefined ? true : false);

  // Filter out undefined values to satisfy exactOptionalPropertyTypes
  const filteredProps = Object.fromEntries(
    Object.entries(props).filter(([, v]) => v !== undefined)
  );

  if (isNativeButton) {
    return (
      <ButtonPrimitive
        data-slot="button"
        className={cn(
          buttonVariants({
            variant,
            size,
            spacing,
            className,
          })
        )}
        nativeButton={true}
        {...(render !== undefined && { render })}
        {...filteredProps}
      />
    );
  }

  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(
        buttonVariants({
          variant,
          size,
          spacing,
          className,
        })
      )}
      nativeButton={false}
      {...(render !== undefined && { render })}
      {...filteredProps}
    />
  );
}

export { Button };
